import {
  RolodexEntryQueryBody,
  useRolodexEntries,
} from 'features/rolodex/hooks/useRolodexEntries'
import { useRolodexSignedURLs } from 'features/rolodex/hooks/useRolodexSignedURLs'
import { useMemo } from 'react'
import useSWRImmutable from 'swr/immutable'
import { RolodexEntryType, RolodexFilePath } from 'types'
import { z } from 'zod'

/**
 * Hand-written zod mirror of the `fim-revision-diff-detail` Rolodex entry
 * contract. The producer emits explicit JSON `null` for absent optionals, so
 * every optional is `nullish` — `optional` alone rejects `null` and would
 * silently drop the whole entry/file on `safeParse`.
 */
const BoxSchema = z.object({
  x0: z.number(),
  y0: z.number(),
  x1: z.number(),
  y1: z.number(),
})

export type RevisionDiffDetailBox = z.infer<typeof BoxSchema>

const ShapeSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('rect'),
    p: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  }),
  z.object({
    t: z.literal('line'),
    p: z.array(z.tuple([z.number(), z.number()])),
  }),
])

export type RevisionDiffDetailShape = z.infer<typeof ShapeSchema>

const countOrZero = z
  .number()
  .nullish()
  .transform((value) => value ?? 0)

const DetailCountsSchema = z.object({
  by_status: z
    .object({
      added: countOrZero,
      removed: countOrZero,
      changed: countOrZero,
      matched: countOrZero,
    })
    .nullish(),
  by_impact: z.record(z.string(), z.number()).nullish(),
  by_category: z.record(z.string(), z.number()).nullish(),
})

export const FimRevisionDiffDetailDataSchema = z.object({
  site_id: z.string(),
  from_document_id: z.string(),
  from_page_num: z.number(),
  to_document_id: z.string(),
  to_page_num: z.number(),
  from_revision: z.string().nullish(),
  to_revision: z.string().nullish(),
  label: z.string().nullish(),
  generator: z.string().nullish(),
  generated_at: z.string().nullish(),
  counts: DetailCountsSchema.nullish(),
  verdict: z.string().nullish(),
})

export type FimRevisionDiffDetailData = z.infer<
  typeof FimRevisionDiffDetailDataSchema
>

export type FimRevisionDiffDetailEntry = {
  entryId: string
  data: FimRevisionDiffDetailData
}

export const REVISION_OBJECT_STATUS_SCHEMA = z.enum([
  'added',
  'removed',
  'changed',
  'matched',
])

export type RevisionObjectStatus = z.infer<typeof REVISION_OBJECT_STATUS_SCHEMA>

const DetailObjectSchema = z.object({
  id: z.string(),
  status: REVISION_OBJECT_STATUS_SCHEMA,
  from_box: BoxSchema.nullish(),
  to_box: BoxSchema.nullish(),
  from_shapes: z.array(ShapeSchema).nullish(),
  to_shapes: z.array(ShapeSchema).nullish(),
  node_id: z.string().nullish(),
  cls: z.string().nullish(),
  title: z.string().nullish(),
})

const DetailEventSchema = z.object({
  id: z.string(),
  headline: z.string(),
  details: z.string(),
  kind: z.string().nullish(),
  impact: z.string().nullish(),
  category: z.string().nullish(),
  action: z.string().nullish(),
  tier: z.string().nullish(),
  confidence: z.string().nullish(),
  old_text: z.string().nullish(),
  new_text: z.string().nullish(),
  pixel_evidence: z.record(z.string(), z.unknown()).nullish(),
  cloud_state: z.string().nullish(),
  from_box: BoxSchema.nullish(),
  to_box: BoxSchema.nullish(),
  object_refs: z.array(z.string()).nullish(),
  card_refs: z.array(z.number()).nullish(),
})

export type RevisionDiffDetailEvent = z.infer<typeof DetailEventSchema>

const DetailViewSectionSchema = z.object({
  section: z.string(),
  text: z.string(),
  where: z.string().nullish(),
  event_ids: z.array(z.string()).nullish(),
})

const CloudCheckSchema = z.object({
  source: z.string().nullish(),
  available: z.boolean().nullish(),
  n_clouds: z.number().nullish(),
  n_triangles: z.number().nullish(),
  clouded_areas_without_events: z.array(BoxSchema).nullish(),
  triangles_without_events: z.array(BoxSchema).nullish(),
})

export const RevisionDiffDetailsFileSchema = z.object({
  objects: z.array(DetailObjectSchema),
  events: z.array(DetailEventSchema),
  view: z.array(DetailViewSectionSchema),
  cloud_check: CloudCheckSchema.nullish(),
})

export type RevisionDiffDetailsFile = z.infer<
  typeof RevisionDiffDetailsFileSchema
>

function parseDetailData(data: unknown): FimRevisionDiffDetailData | undefined {
  const result = FimRevisionDiffDetailDataSchema.safeParse(data)
  return result.success ? result.data : undefined
}

/**
 * Loads all `fim-revision-diff-detail` entries whose from AND to documents are
 * both in `bqDocumentIds`. An array tag value is an OR in Rolodex, so one query
 * per side covers every pairing of the family; the from/to sides are two query
 * bodies merged and deduped by entry id. Called once by the compare launcher,
 * which passes the result down.
 */
export function useRevisionDiffDetailsForDocument(
  bqDocumentIds: readonly string[] | undefined,
  siteId: string | undefined
): {
  details: FimRevisionDiffDetailEntry[]
  isLoading: boolean
} {
  const idsKey = bqDocumentIds?.length
    ? [...bqDocumentIds].sort().join('|')
    : undefined

  const sortedIds = useMemo(() => (idsKey ? idsKey.split('|') : []), [idsKey])

  const queries = useMemo<RolodexEntryQueryBody[] | undefined>(() => {
    if (!idsKey || !siteId) return undefined
    const fromQuery: RolodexEntryQueryBody = {
      type: RolodexEntryType.REVISION_DIFF_DETAIL,
      tags: { site: [siteId], from_document_id: sortedIds },
    }
    const toQuery: RolodexEntryQueryBody = {
      type: RolodexEntryType.REVISION_DIFF_DETAIL,
      tags: { site: [siteId], to_document_id: sortedIds },
    }
    return [fromQuery, toQuery]
  }, [idsKey, siteId, sortedIds])

  const { entries, isLoading } = useRolodexEntries(queries)

  const details = useMemo(() => {
    const idSet = new Set(sortedIds)
    const seenEntryIds = new Set<string>()
    const result: FimRevisionDiffDetailEntry[] = []
    for (const entry of entries) {
      if (entry.type !== RolodexEntryType.REVISION_DIFF_DETAIL) continue
      if (seenEntryIds.has(entry.id)) continue
      seenEntryIds.add(entry.id)
      const data = parseDetailData(entry.data)
      if (!data) continue
      if (!idSet.has(data.from_document_id)) continue
      if (!idSet.has(data.to_document_id)) continue
      result.push({ entryId: entry.id, data })
    }
    return result
  }, [entries, sortedIds])

  return { details, isLoading }
}

async function fetchDetailsFile(
  url: string
): Promise<RevisionDiffDetailsFile | undefined> {
  const response = await fetch(url)
  if (!response.ok) {
    const body = await response.text().catch(() => '')
    throw new Error(
      `Failed to load details.json (${response.status}): ${body.slice(0, 200)}`
    )
  }
  const json: unknown = await response.json()
  const result = RevisionDiffDetailsFileSchema.safeParse(json)
  return result.success ? result.data : undefined
}

/**
 * Resolves the heavy `details.json` attachment of a detail entry. Only
 * fetched once `enabled` is true so the eager launcher query stays cheap.
 * A fetch or parse failure yields no file.
 */
export function useRevisionDiffDetailFile(
  entry: FimRevisionDiffDetailEntry | undefined,
  options: { enabled: boolean }
): {
  detailsFile: RevisionDiffDetailsFile | undefined
  isLoading: boolean
  error: unknown
} {
  const attachmentRef: RolodexFilePath | undefined = useMemo(() => {
    if (!entry || !options.enabled) return undefined
    return { entry_id: entry.entryId, path: 'details.json' }
  }, [entry, options.enabled])

  const { url, isLoading: isLoadingUrl } = useRolodexSignedURLs(attachmentRef)

  const { data, isLoading, error } = useSWRImmutable(
    url ? [url, 'revision-diff-details'] : null,
    ([fetchUrl]) => fetchDetailsFile(fetchUrl)
  )

  if (!entry) {
    return { detailsFile: undefined, isLoading: false, error: undefined }
  }
  return { detailsFile: data, isLoading: isLoadingUrl || isLoading, error }
}
